import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { dailyReport, projectDetails } from '../../../../../../../../core/projects.selectors';
import { appDataDetails } from '../../../../../../../../../../shared/core/shared.selectors';
import { DailyReportService } from '../../../../daily-report.service';

@Component({
  standalone: false,
  selector: 'app-add-contractor-dialog',
  templateUrl: './add-contractor-dialog.component.html',
  styleUrls: ['./add-contractor-dialog.component.scss'],
})
export class AddContractorDialogComponent implements OnInit, OnDestroy {
  form = new FormGroup<any>({
    sub_contractor: new FormControl(null),
  });
  subContractorOptions: any[] = [];
  contractorFormFields: any[] = [];
  errorMessages: any = {};
  private existingContractorIds: string[] = [];
  private allProjectContractors: any[] = [];
  private fieldsInitialized = false;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    public dialogRef: MatDialogRef<AddContractorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<fromRoot.State>,
    private dailyReportService: DailyReportService,
  ) {}

  ngOnInit() {
    this.store.select(dailyReport)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((report: any) => {
        const existing = report?.reportDetails?.sub_contractors || [];
        this.existingContractorIds = existing.map(sc => sc.sub_contractor?.id).filter(Boolean);
        this.filterContractorOptions();
      });

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.allProjectContractors = project?.sub_contractors || [];
        this.filterContractorOptions();
      });

    this.store.select(appDataDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        const fields = data?.dailyReportFields?.sub_contractor?.fields || [];
        this.contractorFormFields = [...fields].sort((a, b) => a.display_order - b.display_order);
        this.initFormControls();
      });
  }

  private filterContractorOptions() {
    // If restricted list passed via dialog data, use only those contractors
    if (this.data?.restrictedContractors?.length) {
      this.subContractorOptions = this.data.restrictedContractors
        .filter(c => !this.existingContractorIds.includes(c.id))
        .map(c => ({ value: c.id, label: c.name }));
    } else {
      this.subContractorOptions = this.allProjectContractors.filter(
        c => !this.existingContractorIds.includes(c.value)
      );
    }
  }

  private initFormControls() {
    if (this.fieldsInitialized) return;
    this.fieldsInitialized = true;

    this.contractorFormFields.forEach(field => {
      const fieldType = field.componentType || field.type;
      if (fieldType === 'time') {
        this.form.addControl(field.name, new FormControl(null));
        this.form.addControl(`${field.name}_am_pm`, new FormControl(null));
        this.form.addControl(`${field.name}_timePeriod`, new FormControl(null));
      } else {
        this.form.addControl(field.name, new FormControl(null));
      }
    });
  }

  private convertTo24h(timePeriod: string, amPm: string): string | null {
    if (!timePeriod || !amPm) return null;
    const parts = timePeriod.split(':');
    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    if (amPm === 'PM' && hours < 12) hours += 12;
    if (amPm === 'AM' && hours === 12) hours = 0;
    return `${String(hours).padStart(2, '0')}:${minutes}:00`;
  }

  onCancel() {
    this.dialogRef.close(null);
  }

  onSubmit() {
    const contractorId = this.form.controls['sub_contractor'].value;
    if (!contractorId) {
      this.form.controls['sub_contractor'].setErrors({ required: true });
      this.form.controls['sub_contractor'].markAsTouched();
      this.errorMessages = { sub_contractor: 'Contractor is required' };
      return;
    }
    this.errorMessages = {};

    const values = this.form.getRawValue();

    const fieldValues = this.contractorFormFields
      .map(field => {
        const fieldType = field.componentType || field.type;
        let value = values[field.name];

        if (fieldType === 'time') {
          value = this.convertTo24h(values[`${field.name}_timePeriod`], values[`${field.name}_am_pm`]);
        } else if (fieldType === 'boolean') {
          value = value != null ? String(value) : null;
        }

        if (value == null) return null;

        return { id: field.id, value: String(value) };
      })
      .filter(Boolean);

    const payload: any = {
      id: `new_${new Date().getTime()}`,
      sub_contractor_id: contractorId,
      field_values: fieldValues,
      pictures: [],
      videos: [],
      comment: null,
    };

    this.dailyReportService.save.next({
      type: 'sub_contractors',
      data: [payload],
      options: { fromDialog: true },
    });

    this.dialogRef.close({
      sub_contractor_id: contractorId,
    });
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
